import React, { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Trash2, Plus, Minus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useLang, STR, loc } from "@/i18n/translations";
import api from "@/lib/api";

export function CartDrawer() {
  const { items, remove, setQty, total, open, setOpen, clear } = useCart();
  const { t, lang } = useLang();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        customer_name: name,
        customer_email: email,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error(t({ fr: "Erreur lors du paiement.", en: "Checkout error." }));
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-obsidian border-l border-gold/25 text-cream w-full sm:max-w-md flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-cream">{t(STR.common.cart)}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto mt-6 space-y-4 pr-1">
          {items.length === 0 && <p className="text-cream/50 text-center py-16">{t(STR.common.emptyCart)}</p>}
          {items.map((i) => (
            <div key={i.id} className="flex gap-3 border border-gold/15 rounded-lg p-3" data-testid={`cart-item-${i.id}`}>
              <img src={i.image} alt={loc(i, "name", lang)} className="w-16 h-16 object-cover rounded" />
              <div className="flex-1">
                <p className="text-sm font-medium">{loc(i, "name", lang)}</p>
                <p className="text-gold text-sm">{i.price.toFixed(2)} €</p>
                <div className="flex items-center gap-2 mt-2">
                  <button onClick={() => setQty(i.id, i.quantity - 1)} className="w-6 h-6 border border-gold/30 rounded flex items-center justify-center hover:border-gold" data-testid={`cart-dec-${i.id}`}><Minus className="w-3 h-3" /></button>
                  <span className="text-sm w-6 text-center">{i.quantity}</span>
                  <button onClick={() => setQty(i.id, i.quantity + 1)} className="w-6 h-6 border border-gold/30 rounded flex items-center justify-center hover:border-gold" data-testid={`cart-inc-${i.id}`}><Plus className="w-3 h-3" /></button>
                </div>
              </div>
              <button onClick={() => remove(i.id)} className="text-cream/40 hover:text-crimson-accent self-start" data-testid={`cart-remove-${i.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>

        {items.length > 0 && (
          <div className="border-t border-gold/20 pt-4 mt-4 space-y-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t(STR.booking.fields.name)} data-testid="cart-name-input" className="w-full bg-white/5 border border-gold/20 rounded px-3 py-2.5 text-sm outline-none focus:border-gold" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="cart-email-input" className="w-full bg-white/5 border border-gold/20 rounded px-3 py-2.5 text-sm outline-none focus:border-gold" />
            <div className="flex justify-between text-lg">
              <span>{t(STR.common.total)}</span>
              <span className="text-gold font-semibold">{total.toFixed(2)} €</span>
            </div>
            <button onClick={checkout} disabled={loading} data-testid="checkout-btn" className="btn-gold w-full rounded py-3 flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {t(STR.common.checkout)}
            </button>
            <button onClick={clear} className="w-full text-xs text-cream/40 hover:text-cream" data-testid="cart-clear-btn">{t({ fr: "Vider le panier", en: "Clear cart" })}</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
